import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Upload, 
  Film, 
  X, 
  CheckCircle, 
  AlertCircle,
  FileVideo
} from 'lucide-react';
import { formatFileSize, formatDuration } from '../utils/helpers';
import { useStore } from '../hooks/useStore';

const ALLOWED_EXTENSIONS = ['.mp4', '.avi', '.mov', '.webm', '.mkv'];
const BROWSER_PLAYABLE = ['.mp4', '.webm', '.mov'];
const MAX_SIZE = 500 * 1024 * 1024; // 500MB

function getFileExtension(filename: string): string {
  const parts = filename.split('.');
  if (parts.length < 2) return '';
  return '.' + (parts.pop()?.toLowerCase() || '');
}

function canBrowserPlay(filename: string): boolean {
  return BROWSER_PLAYABLE.includes(getFileExtension(filename));
}

function customValidator(file: File) {
  const ext = getFileExtension(file.name);
  if (!ext || !ALLOWED_EXTENSIONS.includes(ext)) {
    return { code: 'file-invalid-type', message: 'Invalid file type' };
  }
  if (file.size > MAX_SIZE) {
    return { code: 'file-too-large', message: 'File too large' };
  }
  return null;
}

interface UploadAreaProps {
  onFileSelect: (file: File) => Promise<boolean>;
  disabled?: boolean;
}

export function UploadArea({ onFileSelect, disabled = false }: UploadAreaProps) {
  const { file, videoMetadata, status } = useStore();
  const [isDragActive, setIsDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    setError(null);
    
    if (acceptedFiles.length === 0) {
      return;
    }

    const selectedFile = acceptedFiles[0];
    const success = await onFileSelect(selectedFile);
    
    if (!success) {
      setError('Invalid file. Please select a valid video file (MP4, AVI, MOV, WebM)');
    }
  }, [onFileSelect]);

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    accept: {
      'video/*': ALLOWED_EXTENSIONS,
    },
    validator: customValidator,
    multiple: false,
    disabled: disabled || status !== 'idle',
    onDragEnter: () => setIsDragActive(true),
    onDragLeave: () => setIsDragActive(false),
    onDropAccepted: () => setIsDragActive(false),
    onDropRejected: (rejections) => {
      setIsDragActive(false);
      const code = rejections[0]?.errors[0]?.code;
      if (code === 'file-too-large') {
        setError('File too large. Maximum size: 500MB');
      } else {
        setError(`Invalid format. Allowed: ${ALLOWED_EXTENSIONS.join(', ')}`);
      }
    }
  });

  const clearFile = useCallback(() => {
    useStore.getState().setFile(null);
    useStore.getState().setVideoMetadata(null);
    setError(null);
  }, []);

  const isDisabled = disabled || status !== 'idle';

  return (
    <div className="w-full">
      <AnimatePresence mode="wait">
        {!file ? (
          <motion.div
            key="dropzone"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <div
              {...getRootProps()}
              className={`
                relative overflow-hidden
                border-2 border-dashed rounded-2xl p-12
                transition-all duration-300 cursor-pointer
                ${isDragActive 
                  ? 'border-primary-500 bg-primary-500/10 scale-[1.02]' 
                  : 'border-dark-600 hover:border-primary-500/50 bg-dark-900/50'
                }
                ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}
              `}
            >
              <input {...getInputProps()} />
              
              {/* Background gradient effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary-500/5 via-transparent to-accent-500/5 pointer-events-none" />
              
              {/* Scan line effect when dragging */}
              {isDragActive && (
                <motion.div
                  className="absolute inset-0 bg-gradient-to-b from-primary-500/20 via-primary-500/5 to-transparent"
                  animate={{ y: ['0%', '100%'] }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
                />
              )}

              <div className="relative flex flex-col items-center text-center space-y-6">
                {/* Icon */}
                <motion.div
                  className={`
                    p-6 rounded-2xl
                    ${isDragActive 
                      ? 'bg-primary-500/20 neon-glow' 
                      : 'bg-dark-800/80'
                    }
                  `}
                  animate={isDragActive ? { scale: [1, 1.1, 1] } : {}}
                  transition={{ duration: 0.5, repeat: isDragActive ? Infinity : 0 }}
                >
                  <Upload 
                    className={`w-12 h-12 ${isDragActive ? 'text-primary-400' : 'text-dark-400'}`}
                  />
                </motion.div>

                {/* Text */}
                <div className="space-y-2">
                  <h3 className="text-xl font-semibold text-dark-100">
                    {isDragActive ? 'Drop your video here' : 'Upload Video for Analysis'}
                  </h3>
                  <p className="text-dark-400 text-sm">
                    Drag & drop or{' '}
                    <button 
                      type="button"
                      onClick={open}
                      className="text-primary-400 hover:text-primary-300 underline focus:outline-none"
                    >
                      browse files
                    </button>
                  </p>
                </div>

                {/* Supported formats */}
                <div className="flex flex-wrap justify-center gap-2">
                  {['MP4', 'AVI', 'MOV', 'WebM', 'MKV'].map((format) => (
                    <span
                      key={format}
                      className="px-3 py-1 text-xs font-medium bg-dark-800 text-dark-300 rounded-full"
                    >
                      {format}
                    </span>
                  ))}
                  <span className="px-3 py-1 text-xs font-medium bg-dark-800 text-dark-300 rounded-full">
                    Max 500MB
                  </span>
                </div>
              </div>
            </div>

            {/* Error message */}
            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="mt-4 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center gap-3"
                >
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                  <span className="text-red-300 text-sm">{error}</span>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        ) : (
          <motion.div
            key="preview"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="glass rounded-2xl p-6"
          >
            <div className="flex items-start gap-6">
              {/* Video icon/thumbnail */}
              <div className="flex-shrink-0 p-4 bg-primary-500/10 rounded-xl">
                <FileVideo className="w-10 h-10 text-primary-400" />
              </div>

              {/* File info */}
              <div className="flex-grow min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <h4 className="text-lg font-semibold text-dark-100 truncate">
                    {file.name}
                  </h4>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-dark-500">Size</span>
                    <p className="text-dark-200 font-medium">{formatFileSize(file.size)}</p>
                  </div>
                  {videoMetadata?.duration && (
                    <div>
                      <span className="text-dark-500">Duration</span>
                      <p className="text-dark-200 font-medium">
                        {formatDuration(videoMetadata.duration)}
                      </p>
                    </div>
                  )}
                  {videoMetadata?.width && videoMetadata?.height && (
                    <div>
                      <span className="text-dark-500">Resolution</span>
                      <p className="text-dark-200 font-medium">
                        {videoMetadata.width}×{videoMetadata.height}
                      </p>
                    </div>
                  )}
                  <div>
                    <span className="text-dark-500">Format</span>
                    <p className="text-dark-200 font-medium uppercase">
                      {file.name.split('.').pop()}
                    </p>
                  </div>
                </div>
              </div>

              {/* Clear button */}
              {status === 'idle' && (
                <button
                  onClick={clearFile}
                  className="p-2 text-dark-400 hover:text-dark-200 hover:bg-dark-700 rounded-lg transition-colors"
                  title="Remove file"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>

            {/* Video preview */}
            {file && status === 'idle' && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="mt-6 rounded-xl overflow-hidden bg-dark-950"
              >
                {canBrowserPlay(file.name) ? (
                  <video
                    src={URL.createObjectURL(file)}
                    controls
                    className="w-full max-h-[300px] object-contain"
                  >
                    <track kind="captions" />
                  </video>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-dark-400 gap-3">
                    <FileVideo className="w-16 h-16 text-primary-500/40" />
                    <p className="text-sm">Preview not available for <span className="font-semibold text-dark-200">{file.name.split('.').pop()?.toUpperCase()}</span> format</p>
                    <p className="text-xs text-dark-500">The video will be processed normally on the server</p>
                  </div>
                )}
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Features list */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4"
      >
        {[
          { icon: Film, title: 'AI Detection', desc: 'Advanced vehicle analysis' },
          { icon: CheckCircle, title: 'Speed Tracking', desc: 'Real-time velocity calculation' },
          { icon: AlertCircle, title: 'Export Reports', desc: 'PDF, CSV, JSON formats' }
        ].map((feature, i) => (
          <div
            key={i}
            className="flex items-center gap-3 p-4 bg-dark-900/30 rounded-xl border border-dark-800/50"
          >
            <feature.icon className="w-5 h-5 text-primary-400" />
            <div>
              <p className="text-sm font-medium text-dark-200">{feature.title}</p>
              <p className="text-xs text-dark-500">{feature.desc}</p>
            </div>
          </div>
        ))}
      </motion.div>
    </div>
  );
}

export default UploadArea;
